/**
 * Singleton in-flight refresh promise.
 *
 * Prevents concurrent token refresh calls — e.g. ProtectedRoute's own
 * auth check racing the axios interceptor's 401-triggered refresh for
 * the same expired token. PingFed uses single-use refresh token rotation,
 * so two simultaneous calls with the same refresh token cause the losing
 * call to receive "invalid_grant" and clear the tokens the winning call
 * just stored, logging out an actively-used session. All callers share
 * one promise so only one request is ever in flight.
 */
let refreshPromise: Promise<boolean> | null = null

// Set to true once logout() begins. Prevents a concurrent token refresh
// from writing new tokens into sessionStorage after logout has cleared it,
// which would make the app appear still-authenticated after SSO redirects back.
let isLoggingOut = false

// Transient upstream failures during refresh are retried with backoff
// instead of being treated as a terminal invalid_grant.
const TRANSIENT_REFRESH_STATUS_CODES = new Set([502, 503, 504])
const REFRESH_MAX_ATTEMPTS = 3
const REFRESH_BACKOFF_MS = [1000, 2000, 4000]
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
/**
 * localStorage key written by logout() immediately before clearing storage.
 * Other tabs listen for the resulting `storage` event and end their own
 * sessions, ensuring a logout in one tab propagates to all open tabs.
 */
export const AUTH_LOGOUT_BROADCAST_KEY = "auth_logout_broadcast";
export const AUTH_LOGOUT_BROADCAST_CHANNEL = 'auth_logout_channel'

function broadcastCrossTabLogout(): void {
  const timestamp = Date.now().toString()

  // Primary signal for tabs listening via the `storage` event.
  localStorage.setItem(AUTH_LOGOUT_BROADCAST_KEY, timestamp)

  // Fallback signal for browsers/pages where `storage` events are delayed
  // or missed during fast redirect/logout transitions.
  try {
    const channel = new BroadcastChannel(AUTH_LOGOUT_BROADCAST_CHANNEL)
    channel.postMessage({ type: 'logout', timestamp })
    channel.close()
  } catch (error) {
    // Ignore browsers that do not support BroadcastChannel.
    console.warn('BroadcastChannel unavailable for cross-tab logout', error)
  }
}

type RefreshAttemptResult =
  | { outcome: 'success'; data: any }
  | { outcome: 'retry' }
  | { outcome: 'fail' }

// Single POST to the token endpoint, classified into success / transient-retry / terminal-fail
async function performRefreshAttempt(
  tokenUrl: string,
  body: string
): Promise<RefreshAttemptResult> {
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (response.ok) {
    return { outcome: 'success', data: await response.json() }
  }

  if (TRANSIENT_REFRESH_STATUS_CODES.has(response.status)) {
    return { outcome: 'retry' }
  }

  const data = await response.json().catch(() => undefined)
  console.error('Token refresh failed:', data)
  return { outcome: 'fail' }
}

function storeRefreshedTokens(data: {
  access_token: string
  refresh_token?: string
  id_token?: string
}): void {
  // Do not write tokens back if logout has already cleared sessionStorage.
  if (isLoggingOut) return
  sessionStorage.setItem('accessToken', data.access_token)
  if (data.refresh_token) {
    sessionStorage.setItem('refreshToken', data.refresh_token)
  }
  if (data.id_token) {
    sessionStorage.setItem('idToken', data.id_token)
  }
}

function clearStoredTokens(): void {
  sessionStorage.removeItem('accessToken')
  sessionStorage.removeItem('refreshToken')
  sessionStorage.removeItem('idToken')
}

export const authService = {
  // Get OAuth config from window
  getOAuthConfig: () => {
    const envConfig = (globalThis as any).envConfig
    if (!envConfig) {
      throw new Error('Environment config not loaded. Check window.envConfig')
    }

    return {
      clientId: envConfig.clientId,
      redirectUri: `${globalThis.location.origin}/auth/callback`,
      scope: 'openid profile email',
      authUrl: envConfig.authUrl,
      tokenUrl: envConfig.tokenUrl,
      revokeUrl: envConfig.revokeUrl,
      userInfoUrl: envConfig.userInfoUrl,
      jwksUrl: envConfig.jwksUrl,
      endSessionUrl: envConfig.endSessionUrl,
    }
  },

  // Step 1: Redirect user to PingFed login with PKCE
  initiateLogin: () => {

    const OAUTH_CONFIG = authService.getOAuthConfig()

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier()
    generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const state = generateRandomState()

      // Store verifier and state in sessionStorage for callback
      sessionStorage.setItem('oauth_code_verifier', codeVerifier)
      sessionStorage.setItem('oauth_state', state)

      const params = new URLSearchParams({
        response_type: 'code',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        client_id: OAUTH_CONFIG.clientId,
        redirect_uri: OAUTH_CONFIG.redirectUri,
        scope: OAUTH_CONFIG.scope,
        state: state,
      })

      const loginUrl = `${OAUTH_CONFIG.authUrl}?${params.toString()}`

      globalThis.location.href = loginUrl
    })
  },

  /**
   * Redirects the browser to the IdP single-logout endpoint.
   * Used by sibling tabs when they receive a cross-tab logout broadcast.
   */
  redirectToSsoLogout: () => {
    const config = authService.getOAuthConfig()
    const idToken = sessionStorage.getItem('idToken') || ''
    const endSessionUrl = `${config.endSessionUrl}?id_token_hint=${encodeURIComponent(idToken)}`
    globalThis.location.replace(endSessionUrl)
  },

  // Step 2: Exchange authorization code for tokens (with client_secret)
  exchangeCodeForToken: async (code: string) => {
    try {

      const OAUTH_CONFIG = authService.getOAuthConfig()

      const codeVerifier = sessionStorage.getItem('oauth_code_verifier')
      if (!codeVerifier) {
        throw new Error('Code verifier not found in sessionStorage')
      }

      // Use URLSearchParams as per Kaiser example
      const params = new URLSearchParams()
      params.append('grant_type', 'authorization_code')
      params.append('code', code)
      params.append('code_verifier', codeVerifier)
      params.append('redirect_uri', OAUTH_CONFIG.redirectUri)
      params.append('client_id', OAUTH_CONFIG.clientId)

      const response = await fetch(OAUTH_CONFIG.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Token exchange failed:', data)
        throw new Error(
          `Token exchange failed: ${data.error} - ${data.error_description}`
        )
      }

      function extractSubFromJwt(jwt?: string): string | undefined {
        if (!jwt || typeof jwt !== 'string') return undefined
        const parts = jwt.split('.')
        if (parts.length !== 3) return undefined

        // base64url -> base64
        let b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/')
        const pad = b64.length % 4
        if (pad) b64 += '='.repeat(4 - pad)

        try {
          const decoded = atob(b64)
          const payload = JSON.parse(decoded)
          return payload?.sub
        } catch (e) {
          console.warn('Failed to decode JWT payload for sub extraction', e)
          return undefined
        }
      }

      const userId =
        data?.sub ??
        extractSubFromJwt(data?.id_token) ??
        extractSubFromJwt(data?.access_token)
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        idToken: data.id_token,
        expiresIn: data.expires_in,
        userId: userId,
      }
    } catch (error) {
      console.error('Token exchange error:', error)
      throw error
    }
  },

  // Step 3: Refresh access token using refresh token
  refreshAccessToken: async (): Promise<boolean> => {
    // Reuse an existing in-flight refresh rather than sending a second
    // request with the same (soon to be rotated-out) refresh token.
    if (refreshPromise) {
      return refreshPromise
    }

    const doRefresh = async (): Promise<boolean> => {
      const OAUTH_CONFIG = authService.getOAuthConfig()

      const refreshToken = sessionStorage.getItem('refreshToken')
      if (!refreshToken) {
        return false
      }

      const params = new URLSearchParams()
      params.append('grant_type', 'refresh_token')
      params.append('refresh_token', refreshToken)
      params.append('client_id', OAUTH_CONFIG.clientId)
      const body = params.toString()

      // A 502/503/504 (or a network blip) from PingFed is transient — retry
      // with backoff instead of treating it the same as an invalid_grant and
      // forcing a logout mid-session.
      for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
        const isLastAttempt = attempt === REFRESH_MAX_ATTEMPTS

        try {
          const result = await performRefreshAttempt(OAUTH_CONFIG.tokenUrl, body)

          if (result.outcome === 'success') {
            storeRefreshedTokens(result.data)
            return true
          }

          if (result.outcome === 'fail') {
            clearStoredTokens()
            return false
          }

          // outcome === 'retry': fall through to backoff below
        } catch (error) {
          if (isLastAttempt) {
            console.error('Token refresh error:', error)
            return false
          }
          console.warn('Token refresh network error — retrying', error)
        }

        if (!isLastAttempt) {
          await delay(REFRESH_BACKOFF_MS[attempt - 1])
        }
      }

      return false
    }

    // .finally() callbacks always run as a microtask, even on an already-
    // resolved promise — unlike a try/finally inside doRefresh() itself,
    // which (on a fully synchronous path, e.g. the "no refresh token" early
    // return) would run and null out refreshPromise BEFORE this assignment
    // below completes, leaving the module-level lock permanently stuck on
    // a stale resolved promise for the rest of the SPA session.
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })

    return refreshPromise
  },

  isAuthenticated: (): boolean => {
    const token = sessionStorage.getItem('accessToken')
    return !!token && !isTokenExpired(token)
  },

  // Used by the axios interceptor to refresh proactively, before a token
  // that's about to expire is sent and guaranteed to come back as a 401.
  isTokenExpiringSoon: (token: string, bufferMs = 30_000): boolean => {
    return isTokenExpired(token, bufferMs)
  },

  // Check authentication status and attempt refresh if needed
  checkAndRefreshAuth: async (): Promise<boolean> => {
    const accessToken = sessionStorage.getItem('accessToken')
    const refreshToken = sessionStorage.getItem('refreshToken')

    // Scenario 1: No tokens at all - need to login
    if (!accessToken && !refreshToken) {
      return false
    }

    // Scenario 2: Access token exists and valid - user is authenticated
    if (accessToken && !isTokenExpired(accessToken)) {
      return true
    }

    // Scenario 3: Access token expired but refresh token exists - try to refresh
    if ((!accessToken || isTokenExpired(accessToken)) && refreshToken) {
      const refreshed = await authService.refreshAccessToken()
      if (refreshed) {
        return true
      }
    }

    // Scenario 4 & 5: Refresh failed or no refresh token - need to login again
    console.log(
      'Scenario 4/5: Refresh failed or unavailable - user needs to login'
    )
    return false
  },

  getAccessToken: (): string | null => {
    return sessionStorage.getItem('accessToken')
  },

  getRefreshToken: (): string | null => {
    return sessionStorage.getItem('refreshToken')
  },

  getIdToken: (): string | null => {
    return sessionStorage.getItem('idToken')
  },



  logout: async () => {

    // Mark logout in progress so any concurrent token refresh cannot write
    // new tokens into sessionStorage after we clear it.
    isLoggingOut = true
    // Abandon any in-flight refresh so no new callers block on a stale promise.
    refreshPromise = null

    try {
      const config = authService.getOAuthConfig()
      const accessToken = sessionStorage.getItem('accessToken')
      const refreshToken = sessionStorage.getItem('refreshToken')
      const idToken = sessionStorage.getItem('idToken')

      // Step 1: Revoke the access token at PingFed
      if (accessToken) {
        try {

          const revokeParams = new URLSearchParams()
          revokeParams.append('token', accessToken)
          revokeParams.append('client_id', config.clientId)

          const revokeResponse = await fetch(config.revokeUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: revokeParams.toString(),
          })

          console.log(
            'Access token revoke response status:',
            revokeResponse.status
          )
        } catch (error) {
          console.error('Access token revocation failed:', error)
          // Continue anyway - still need to revoke refresh token
        }
      }

      // Step 2: Revoke the refresh token at PingFed
      if (refreshToken) {
        try {

          const revokeRefreshParams = new URLSearchParams()
          revokeRefreshParams.append('token', refreshToken)
          revokeRefreshParams.append('client_id', config.clientId)
          revokeRefreshParams.append('token_type_hint', 'refresh_token')

          const refreshRevokeResponse = await fetch(config.revokeUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: revokeRefreshParams.toString(),
          })

          console.log(
            'Refresh token revoke response status:',
            refreshRevokeResponse.status
          )
        } catch (error) {
          console.error('Refresh token revocation failed:', error)
          // Continue anyway - still need to clear storage and end session
        }
      }

      // Step 3: Clear session storage and notify other tabs.
      sessionStorage.clear()
      // Do not clear localStorage here: removing the key immediately can race
      // sibling tabs and make cross-tab sign-off unreliable.
      broadcastCrossTabLogout()

      // Step 4: End the OAuth session at PingFed using SLO endpoint.
      // NOTE: Do NOT call history.replaceState/pushState before this navigation.
      // Doing so causes React Router to mount the Login component, which calls
      // initiateLogin() asynchronously. That SSO-login navigation races with and
      // overrides this SSO-logout navigation, leaving the user still signed in.
      //
      // The 200ms delay lets the cross-tab broadcast propagate to other tabs and
      // allows the revoke requests to fully settle before this tab navigates away.
      // Without it the SSO session may still be active when the browser hits the
      // SLO endpoint, causing a silent SSO re-authentication on the return trip.
      const endSessionUrl = `${config.endSessionUrl}?id_token_hint=${encodeURIComponent(idToken || '')}`
      setTimeout(() => globalThis.location.replace(endSessionUrl), 200)
    } catch (error) {
      console.error('Logout error:', error)

      // Force cleanup even if error occurs

      sessionStorage.clear()

      broadcastCrossTabLogout()

      // Redirect to login using replace
      globalThis.location.replace(`${globalThis.location.origin}/login`)
    }
  },
}

// Generate PKCE code verifier (43 characters minimum)
function generateCodeVerifier(): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const length = 43

  // Use cryptographically secure random values
  const randomValues = new Uint8Array(length)
  crypto.getRandomValues(randomValues)

  let result = ''
  for (let i = 0; i < length; i++) {
    result += characters.charAt(randomValues[i] % characters.length)
  }
  return result
}

// Generate PKCE code challenge from verifier (SHA256)
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hash = await crypto.subtle.digest('SHA-256', data)

  // Convert to base64url
  const bytes = new Uint8Array(hash)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i])
  }

  const codeChallenge = btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '')
  return codeChallenge
}

// Helper: Generate random state for CSRF protection
function generateRandomState(): string {
  const randomBytes = new Uint8Array(32)
  crypto.getRandomValues(randomBytes)

  // Convert to hex string
  const state = Array.from(randomBytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

  sessionStorage.setItem('oauth_state', state)
  return state
}

// Helper: Check if token is expired, optionally treating a token within
// bufferMs of its exp as already expired (clock skew / in-flight latency)
function isTokenExpired(token: string, bufferMs = 0): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (!payload?.exp) return true

    const expirationTime = payload.exp * 1000
    return Date.now() >= expirationTime - bufferMs
  } catch (error) {
    console.warn('Failed to decode token for expiration check', error)
    return true
  }
}
